import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { PageTitle } from '../../../../../_metronic/layout/core'
import { FilterManager } from '../../../../common/filter/FilterManager'
import { TablePagination } from '../../../../../components/Table/TablePagination'
import AuditDateRangeFilter from './components/AuditDateRangeFilter'
import RequisiteAuditTable from './components/RequisiteAuditTable'
import { getEmployeeLookup, getRequisiteAudit } from './core/api'
import { CATEGORY_OPTIONS, RequisiteAuditCategory } from './core/consts'
import { IEmployeeLookupItem, IRequisiteAuditItem } from './core/response.types'
import { RequisiteAuditFilters } from './core/types'
import { convertToUTCByMSK } from '../../../../../utils'
import { IsGranted, ROLE, useAuth } from '../../../auth'

const PAGE_SIZE = 200

const DEFAULT_FILTERS: RequisiteAuditFilters = { dateRange: {}, category: '', blameId: '' }

const RequisiteAuditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const requisiteId = parseInt(id ?? '0', 10)
  const { currentUser } = useAuth()
  const canFilterByEmployee = IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser)

  const [items, setItems] = useState<IRequisiteAuditItem[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<IEmployeeLookupItem[]>([])
  const [filters, setFilters] = useState<RequisiteAuditFilters>(DEFAULT_FILTERS)

  const employeeOptions = useMemo(
    () => employees.map((e) => ({ value: String(e.value), label: e.label })),
    [employees],
  )

  useEffect(() => {
    if (!canFilterByEmployee) return
    getEmployeeLookup()
      .then((r) => {
        if (r.data.success) setEmployees(r.data.data ?? [])
      })
      .catch(() => {})
  }, [canFilterByEmployee])

  const load = useCallback(
    async (pg: number, f: RequisiteAuditFilters) => {
      if (!requisiteId) return
      setLoading(true)
      try {
        const r = await getRequisiteAudit({
          requisiteId,
          dateStart: f.dateRange?.from ? convertToUTCByMSK(f.dateRange.from) : undefined,
          dateEnd: f.dateRange?.to ? convertToUTCByMSK(f.dateRange.to) : undefined,
          category: (f.category || undefined) as RequisiteAuditCategory | undefined,
          blameId: f.blameId || undefined,
          page: pg + 1,
          perPage: PAGE_SIZE,
        })
        if (r.data.success) {
          setItems(r.data.data ?? [])
          setTotalRows(r.data.totalRows ?? 0)
        } else {
          setItems([])
          setTotalRows(0)
        }
      } catch {
        setItems([])
        setTotalRows(0)
      } finally {
        setLoading(false)
      }
    },
    [requisiteId],
  )

  useEffect(() => {
    load(0, {})
  }, [load])

  const handleApplyFilters = (f: RequisiteAuditFilters) => {
    setFilters(f)
    setPage(0)
    load(0, f)
  }

  const handlePageChange = (selected: number) => {
    setPage(selected)
    load(selected, filters)
  }

  const pageCount = Math.ceil(totalRows / PAGE_SIZE)

  return (
    <>
      <PageTitle>{`История изменений реквизита #${requisiteId}`}</PageTitle>
      <FilterManager<RequisiteAuditFilters>
        activeFilters={[
          'dateRange',
          { filter: 'select', fieldName: 'category' },
          { filter: 'select', fieldName: 'blameId', visibility: canFilterByEmployee },
        ]}
        defaultValues={DEFAULT_FILTERS}
        customFilters={{
          dateRange: { component: AuditDateRangeFilter as any, defaultValue: {} as any },
        }}
        filterProps={{
          dateRange: { hideRanges: true, className: 'w-100' },
          category: { options: CATEGORY_OPTIONS, label: 'Категория события', placeholder: 'Все' },
          blameId: { options: employeeOptions, label: 'Сотрудник', placeholder: 'Все' },
        }}
        onApplyFilters={handleApplyFilters}
      />
      <div className='card'>
        <div className='card-body'>
          <RequisiteAuditTable items={items} loading={loading} />
          <TablePagination
            currentPage={page}
            pageCount={pageCount}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </>
  )
}

export default RequisiteAuditPage
